<template>
    <div class="filter">
        <div class="header-container">
        <h2 v-if="!hideHeader">{{ $t('title.search') }}</h2>
        <AccordionList class="container-accordion">
            <AccordionItem
                class="cursor-pointer"
                @click="toggleAccordion"
                @keydown.enter="toggleAccordion"
                @keydown.space.prevent="toggleAccordion"
                :open="accordionOpen"
                tabindex="0"
            >
                <template #summary>{{ $t('facture.suivi.criteresAvances') }}</template>
                <fieldset class="filter-fieldset filterAndButtons" @click.stop>
                    <legend class="filter-legend">{{ $t('title.search') }}</legend>
                    <div class="filters">

                        <div class="inputWrap" v-if="filter.numeroFacture !== undefined">
                            <TextInput class="filterInput" v-model="filterNumeroFacture" :label="$t('facture.filter.numeroFacture')" />
                        </div>

                        <div class="inputWrap" v-if="filter.societe !== undefined">
                            <TextInput class="filterInput" v-model="filterSociete" :label="$t('facture.filter.societe')" />
                        </div>

                        <div class="inputWrap" v-if="filter.etablissement !== undefined">
                            <TextInput class="filterInput" v-model="filterEtablissement" :label="$t('facture.filter.etablissement')" />
                        </div>

                        <div class="inputWrap" v-if="filter.fournisseur !== undefined">
                            <TextInput class="filterInput" v-model="filterFournisseur" :label="$t('facture.filter.fournisseur')" />
                        </div>

                        <div class="full-width-break"></div>

                        <div class="dateInputWrap" v-if="filter.dateFrom !== undefined">
                            <DatePicker
                                :date-max="filter.dateTo"
                                :dateValue="filter.dateFrom"
                                :label="$t('facture.filter.dateFrom')"
                                @setDate="setFilterDateFrom"
                            />
                        </div>
                        <div class="dateInputWrap" v-if="filter.dateTo !== undefined">
                            <DatePicker
                                :dateValue="filter.dateTo"
                                :label="$t('facture.filter.dateTo')"
                                @setDate="setFilterDateTo"
                                :dateMin="filter.dateFrom"
                            />
                            <ReglePastille cle="facturation.filtreDatesIncoherentes" placement="bottom" />
                        </div>

                        <div class="" v-if="comparisonList !== undefined">
                            <DropDown
                                :label="$t('facture.filter.montantTTC')"
                                :list="comparisonList"
                                :selectedItem="filter.comparison"
                                :display="displayType"
                                @select="selectComparison"
                                class="width-250px"
                            />
                        </div>
                        <div class="" v-if="comparisonList !== undefined && filter.comparison !== $t('facture.suivi.dropdown.novalue')">
                            <div class="inputWrap noMarginLeft">
                                <NumberInput
                                    class="filterInput"
                                    v-model="filterMontant"
                                    :label="$t('facture.filter.montantTTC')"
                                    :class="filterMontant !== '' ? 'montant' : ''"
                                />
                            </div>
                        </div>

                        <div class="inputWrap" v-if="filter.joursRetard !== undefined">
                            <NumberInput
                                class="filterInput"
                                v-model="filterJoursRetard"
                                :label="$t('facture.filter.joursRetard')"
                            />
                        </div>
                    </div>
                    <div class="btnWrapper" v-if="!hideButton">
                        <div class="btns">
                            <ButtonWhite :onClick="clearFilter" class="clearBtn" :text="$t('button.clear')" />
                            <ButtonBlue :disabled="disabled" :onClick="search" class="searchBtn" :text="$t('button.search')" />
                        </div>
                    </div>
                </fieldset>
            </AccordionItem>
        </AccordionList>
    </div>
    </div>
</template>

<script lang="ts">
import ButtonBlue from '@/components/ui-components/ButtonBlue.vue';
import ButtonWhite from '@/components/ui-components/ButtonWhite.vue';
import DatePicker from '@/components/ui-components/DatePicker.vue';
import TextInput from '@/components/ui-components/TextInput.vue';
import NumberInput from '@/components/ui-components/NumberInput.vue';
import { defineComponent } from 'vue';
import DropDown from '@/components/ui-components/DropDown.vue';
import { AccordionItem, AccordionList } from 'vue3-rich-accordion';
import ReglePastille from '@/components/regles/ReglePastille.vue';

export interface FactureFilter {
    societe?: string;
    etablissement?: string;
    fournisseur?: string;
    numeroFacture?: string;
    dateFrom?: string;
    dateTo?: string;
    comparison?: string;
    montant?: number | null;
    joursRetard?: number | null;
}

export default defineComponent({
    name: 'FilterSuiviFacture',
    components: {
        TextInput,
        ButtonWhite,
        ButtonBlue,
        DatePicker,
        NumberInput,
        DropDown,
        AccordionList,
        AccordionItem,
        ReglePastille
    },
    props: {
        filter: { type: Object, required: true },
        hideButton: { type: Boolean, required: false },
        hideHeader: { type: Boolean, required: false },
        comparisonList: { type: Array, required: false },
        disabled: { type: Boolean, required: false, default: false }
    },
    data() {
        return {
            debounce: 0,
            accordionOpen: false as boolean
        };
    },
    computed: {
        filterSociete: {
            get(): string {
                return this.filter.societe || '';
            },
            set(value: string) {
                this.$emit('setSociete', value);
            }
        },
        filterEtablissement: {
            get(): string {
                return this.filter.etablissement || '';
            },
            set(value: string) {
                this.$emit('setEtablissement', value);
            }
        },
        filterFournisseur: {
            get(): string {
                return this.filter.fournisseur || '';
            },
            set(value: string) {
                this.$emit('setFournisseur', value);
            }
        },
        filterNumeroFacture: {
            get(): string {
                return this.filter.numeroFacture || '';
            },
            set(value: string) {
                this.$emit('setNumeroFacture', value);
            }
        },
        filterMontant: {
            get(): string {
                return this.filter.montant ? this.filter.montant.toString() : '';
            },
            set(value: string) {
                this.$emit('setMontant', value ? parseFloat(value) : null);
            }
        },
        filterJoursRetard: {
            get(): string {
                return this.filter.joursRetard ? this.filter.joursRetard.toString() : '';
            },
            set(value: string) {
                this.$emit('setJoursRetard', value ? parseInt(value) : null);
            }
        }
    },
    methods: {
        toggleAccordion(): void {
            this.accordionOpen = !this.accordionOpen;
        },
        displayType(item: any): any {
            return item;
        },
        setFilterDateFrom(value: string): void {
            this.$emit('setDateFrom', value);
        },
        setFilterDateTo(value: string): void {
            const to: Date = new Date(value);
            const from: Date = new Date(this.filter.dateFrom || '');
            if (from > to) {
                this.$emit('setDateFrom', '');
            }
            this.$emit('setDateTo', value);
        },
        selectComparison(comparison: string): void {
            this.$emit('comparison', comparison);
        },
        search(): void {
            this.accordionOpen = false;
            this.$emit('search');
        },
        clearFilter(): void {
            this.accordionOpen = false;
            this.$emit('clearFilter');
        },
    }
});
</script>

<style lang="scss" scoped>
@media screen and (max-width: 1631px){
    .filter {
        .filterAndButtons {
            .filters {
                width: 86% !important;
            }

            .btnWrapper {
                width: 12% !important;
            }
        }
    }
}
.header-container {
    display: flex;
    align-items: center;
    justify-content: space-between;

    h2 {
        margin-bottom: 0;
        margin-right: 16px;
    }

    .container-accordion {
        flex-grow: 1;
    }
}
.filter {
    width: 100%;
    h2 {
        font-size: var(--font-size-medium);
        font-weight: 500;
        margin-bottom: 16px;
        color: var(--color-principal);
    }
    .filter-fieldset {
        border: none;
        padding: 0;
        margin: 0;
    }

    .filter-legend {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }

    .filterAndButtons {
        display: flex;
        gap: 16px;
        padding-top: 16px;
        flex-wrap: wrap;
        /* �l�ment invisible qui force le retour � la ligne */
        .full-width-break {
            flex-basis: 100%;
            height: 0;
        }
        .filters {
            width: 80%;
            display: flex;
            flex-wrap: wrap;
            gap: 16px;
            .inputWrap {
                width: 250px;
                &.noMarginLeft {
                    margin-left: 0;
                }
            }
            .dateInputWrap {
                width: 250px;
            }
            .width-250px {
                width: 250px;
            }
        }
        .btnWrapper {
            width: 18%;
            display: flex;
            justify-content: flex-end;
            .btns {
                display: flex;
                flex-wrap: wrap;
                justify-content: center;
                margin-bottom: 8px;
                gap: 8px;
            }

            .clearBtn, .searchBtn {
                display: flex;
                white-space: nowrap;
                justify-content: center;
                align-items: center;
                cursor: pointer;
                border: 1px solid var(--color-principal);
                border-radius: 4px;
                padding: 10px;
                text-transform: uppercase;
                font-weight: 600;
                max-height: 48px;
            }

            .clearBtn {
                background-color: white;
                color: var(--color-principal);
            }

            .searchBtn {
                background-color: var(--color-principal);
                color: white;
                width: 120px;
            }
        }
    }
    .container-accordion {
        cursor: initial;
        margin-bottom: 0;
    }
    .cursor-pointer {
        cursor: pointer;
    }
}
:deep(.accordion-item__summary) {
    color: var(--color-principal);
    font-weight: 500;
    padding: 12px 16px;
}
:deep(.accordion-item__content) {
    padding: 0 16px 16px;
}
</style>
